/**
 * PWA lifecycle helpers.
 * - registerServiceWorker(): wires the SW and prompts a soft reload when a new build is ready.
 * - useInstallPrompt(): hook for the "Install Akomoda" button (Chrome/Edge/Android).
 * - isElectron: true when running inside the desktop wrapper.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

export const isElectron = typeof window !== "undefined" && !!window.akomodaDesktop;

export function registerServiceWorker() {
    if (!("serviceWorker" in navigator)) return;
    if (isElectron) return; // Electron already ships offline shell; don't double-cache.

    window.addEventListener("load", async () => {
        try {
            const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });

            // A new SW is waiting → auto-apply after a short delay so users
            // don't get stranded on stale UI after a deploy.
            const applyUpdate = (worker) => {
                worker.postMessage({ type: "SKIP_WAITING" });
                toast.message("Updating to the latest version…", { duration: 3500 });
                // Reload once the new SW takes control (fires exactly once)
                let refreshing = false;
                navigator.serviceWorker.addEventListener("controllerchange", () => {
                    if (refreshing) return;
                    refreshing = true;
                    window.location.reload();
                });
            };

            if (reg.waiting) applyUpdate(reg.waiting);

            reg.addEventListener("updatefound", () => {
                const worker = reg.installing;
                if (!worker) return;
                worker.addEventListener("statechange", () => {
                    if (worker.state === "installed" && navigator.serviceWorker.controller) {
                        applyUpdate(worker);
                    }
                });
            });

            // Silent hourly update check so long-open tabs pick up server-side deploys
            setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("SW registration failed", e);
        }
    });
}

export function useInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(
        typeof window !== "undefined" && window.matchMedia("(display-mode: standalone)").matches
    );

    useEffect(() => {
        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
        };
        const onInstalled = () => { setDeferred(null); setInstalled(true); };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (!deferred) return "unavailable";
        deferred.prompt();
        const choice = await deferred.userChoice;
        setDeferred(null);
        return choice?.outcome || "dismissed";
    }, [deferred]);

    return { canInstall: !!deferred && !installed && !isElectron, installed, promptInstall };
}
